import {
  HeartOutlined,
  HomeOutlined,
  QuestionCircleOutlined,
  ShareAltOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, Tooltip } from 'antd';
import { getConfigurationUrl } from 'helpers/getConfigurationUrl';
import { openAsPost } from 'helpers/openAsPost';
import { useSmallScreen } from 'helpers/isSmallScreen';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotificationStore } from 'state/notificationstate/notificationstate';
import { clearConfiguration, saveConfiguration } from 'viewer/api';
import { helpMenu } from './helpMenu';
import './options.css';
import { useAppState } from 'state/appstate/appstate';

export type OptionsProps = {
  selectedOptions: Array<'home' | 'share' | 'savePortfolio' | 'help'>;
};

const Options: React.FunctionComponent<OptionsProps> = ({
  selectedOptions,
}) => {
  const getMessageBody = () =>
    `Here is my summerwood project ${getConfigurationUrl()}`;
  const isSmallScreen = useSmallScreen();
  const { t } = useTranslation();
  const [messageBody, setMessageBody] = useState(getMessageBody());
  const style = {
    fontSize: isSmallScreen ? '20px' : '24px',
  };
  const setNotification = useNotificationStore(
    (state) => state.setNotification
  );
  const {
    configurationId,
    styleProduct: {
      style: { id: styleId } = { id: undefined },
      product: { id: productId } = { id: undefined },
    },
    uState: { currency } = { currency: undefined },
  } = useAppState.getState();

  const messageSubject = 'Summerwood project';

  useEffect(() => {
    setMessageBody(getMessageBody());
  }, [configurationId, styleId, productId, currency]);

  const options = {
    home: {
      icon: (
        <Tooltip placement="bottom" title={t('options.home')}>
          <HomeOutlined style={style} />
        </Tooltip>
      ),
      onClickAction: () => {
        clearConfiguration();
      },
      key: 'home',
    },
    share: {
      icon: (
        <Tooltip placement="bottom" title={t('options.share')}>
          <ShareAltOutlined style={style} />
        </Tooltip>
      ),
      onClickAction: async () => {
        try {
          const configuration = await saveConfiguration(false, true);
          setNotification(t('options.portfolioSaved'), 'success', 'small');
          configuration && window.open(configuration.redirectURL);
        } catch (error) {
          setNotification(error?.message, 'error', 'small');
        }
      },
      key: 'share',
    },
    savePortfolio: {
      icon: (
        <Tooltip placement="bottom" title={t('options.heart')}>
          <HeartOutlined style={style} />
        </Tooltip>
      ),
      onClickAction: async () => {
        try {
          const configuration = await saveConfiguration(true);
          setNotification(t('options.portfolioSaved'), 'success', 'small');
          configuration && openAsPost(configuration.redirectURL);
        } catch (error) {
          setNotification(error?.message, 'error', 'small');
        }
      },
      key: 'heart',
    },
    help: {
      icon: (
        <Tooltip placement="left" title={t('options.help')}>
          <div>
            <Dropdown
              overlayStyle={{
                width: isSmallScreen ? '100%' : 'auto',
                minWidth: 188,
                textAlign: isSmallScreen ? 'center' : 'left',
              }}
              overlay={helpMenu}
              placement="bottomCenter"
              trigger={['click']}
            >
              <QuestionCircleOutlined style={style} />
            </Dropdown>
            <div
              style={{
                bottom: 0,
                right: 'calc(50% - 14px)',
                position: 'absolute',
                width: 0,
                height: 0,
                borderLeft: '7px solid transparent',
                borderTop: '7px solid transparent',
                borderBottom: '7px solid #2f2f2f',
              }}
            />
          </div>
        </Tooltip>
      ),
      onClickAction: () => {},
      key: 'question',
    },
  };

  return (
    <div style={{ justifySelf: 'right' }}>
      {selectedOptions.map((o) => {
        const option = options[o];
        return (
          <Button
            className="hover-primary"
            key={o}
            onClick={option.onClickAction}
            type="text"
            style={{
              padding: isSmallScreen ? '4px 6px' : '4px 15px',
              color: Theme['@black'],
            }}
          >
            {option.icon}
          </Button>
        );
      })}
    </div>
  );
};

export default Options;
